import { useState, useEffect, useRef } from 'react';

export const skipFormattingSyntax = (text: string, position: number): number => {
    if (position <= 0 || position >= text.length) return position;

    // Skip past <a ...>...</a> tags — show entire link atomically
    const anchorRegex = /<a\s+[^>]*>[^<]*<\/a>/g;
    let match;
    while ((match = anchorRegex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        if (position > start && position < end) {
            return end;
        }
    }

    // Find the start of the current line
    const lineStart = text.lastIndexOf('\n', position - 1) + 1;
    const fromLineStart = position - lineStart;

    // Heading prefix: #{1,4} followed by a space
    const headingMatch = text.slice(lineStart).match(/^#{1,4} /);
    if (headingMatch && fromLineStart < headingMatch[0].length) {
        return lineStart + headingMatch[0].length;
    }

    // Unordered list prefix: "- "
    if (text.slice(lineStart, lineStart + 2) === '- ' && fromLineStart < 2) {
        return lineStart + 2;
    }

    // Ordered list prefix: "\d+. "
    const orderedMatch = text.slice(lineStart).match(/^\d+\. /);
    if (orderedMatch && fromLineStart < orderedMatch[0].length) {
        return lineStart + orderedMatch[0].length;
    }

    // Horizontal rule: "---" on its own line
    if (text.slice(lineStart, lineStart + 3) === '---'
        && (lineStart + 3 >= text.length || text[lineStart + 3] === '\n')
        && fromLineStart < 3) {
        return lineStart + 3;
    }

    // Bold markers: if position splits a "**" pair, jump past the second *
    if (position > 0 && text[position - 1] === '*' && text[position] === '*') {
        return position + 1;
    }

    return position;
};

export const adjustForHtmlTags = (text: string, position: number): number => {
    if (position <= 0) return position;

    // Don't split inside an anchor element (<a ...>...</a>)
    // Check this first since it's the broader boundary
    const anchorRegex = /<a\s+[^>]*>[^<]*<\/a>/g;
    let match;
    while ((match = anchorRegex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        if (position > start && position < end) {
            return start;
        }
    }

    // Don't split inside a tag itself (between < and >)
    const lastOpen = text.lastIndexOf('<', position - 1);
    if (lastOpen !== -1) {
        const nextClose = text.indexOf('>', lastOpen);
        if (nextClose === -1 || nextClose >= position) {
            return lastOpen;
        }
    }

    // Don't split inside a line that has block-level markdown syntax.
    // stableText and fadingText are rendered independently through renderMessageContent,
    // so if the split falls mid-line in a heading/list/hr, the block element in stableText
    // pushes the fadingText out of alignment.
    const lineStart = text.lastIndexOf('\n', position - 1) + 1;
    const lineEnd = text.indexOf('\n', position);
    const fullLine = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd);

    if (
        /^#{1,4} /.test(fullLine)
        || /^- /.test(fullLine)
        || /^\d+\. /.test(fullLine)
        || /^-{3,}$/.test(fullLine.trim())
    ) {
        return lineStart;
    }

    // Don't split inside a bold region (**...**) or between ** and its content.
    // Find the last ** before or at position and check if it's unclosed.
    const textBeforePosition = text.slice(0, position);
    const lastDoubleStar = textBeforePosition.lastIndexOf('**');
    if (lastDoubleStar !== -1) {
        // Check if this ** is an opening marker (odd occurrence = unclosed)
        const textUpToMarker = text.slice(0, lastDoubleStar);
        const priorCount = (textUpToMarker.match(/\*\*/g) || []).length;
        // Even count means this is an opening **, odd means closing
        if (priorCount % 2 === 0) {
            return lastDoubleStar;
        }
    }

    return position;
};

interface UseAnimatedTextOptions {
    charsPerSecond?: number;
    fadeChars?: number;
}

interface UseAnimatedTextReturn {
    stableText: string;
    fadingText: string;
    isAnimating: boolean;
}

export function useAnimatedText(
    rawText: string,
    isStreaming: boolean,
    options: UseAnimatedTextOptions = {},
): UseAnimatedTextReturn {
    const { charsPerSecond = 150, fadeChars = 8 } = options;

    // Track if this is a fresh mount vs ongoing animation
    const hasInitializedRef = useRef(false);
    const wasStreamingRef = useRef(isStreaming);

    // If mounted with content but not streaming, show everything immediately
    const initialPosition = !hasInitializedRef.current && !isStreaming ? rawText.length : 0;

    const [displayPosition, setDisplayPosition] = useState(initialPosition);
    const [isAnimating, setIsAnimating] = useState(false);

    const positionRef = useRef(initialPosition);
    const lastTickRef = useRef(performance.now());
    const animationFrameRef = useRef<number | null>(null);

    // Mark as initialized after first render
    useEffect(() => {
        hasInitializedRef.current = true;
    }, []);

    // Track streaming state changes
    useEffect(() => {
        wasStreamingRef.current = isStreaming;
    }, [isStreaming]);

    useEffect(() => {
        // If raw text is shorter than our position (e.g., content reset), reset position
        if (rawText.length < positionRef.current) {
            positionRef.current = 0;
            setDisplayPosition(0);
        }

        // If there's text to animate
        if (positionRef.current < rawText.length) {
            setIsAnimating(true);

            const animate = () => {
                const now = performance.now();
                const elapsed = now - lastTickRef.current;
                const charsToAdd = Math.floor((elapsed * charsPerSecond) / 1000);

                if (charsToAdd > 0 && positionRef.current < rawText.length) {
                    positionRef.current = Math.min(positionRef.current + charsToAdd, rawText.length);
                    positionRef.current = skipFormattingSyntax(rawText, positionRef.current);
                    setDisplayPosition(positionRef.current);
                    lastTickRef.current = now;
                }

                if (positionRef.current < rawText.length) {
                    animationFrameRef.current = requestAnimationFrame(animate);
                }
                else {
                    setIsAnimating(false);
                }
            };

            // Reset tick reference when starting animation
            lastTickRef.current = performance.now();
            animationFrameRef.current = requestAnimationFrame(animate);
        }

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [rawText, charsPerSecond]);

    // Split text into stable (fully visible) and fading (animating in) portions
    const displayText = rawText.slice(0, displayPosition);
    const rawStableEnd = Math.max(0, displayPosition - fadeChars);
    const stableEnd = adjustForHtmlTags(displayText, rawStableEnd);
    const stableText = displayText.slice(0, stableEnd);
    const fadingText = displayText.slice(stableEnd);

    return {
        stableText,
        fadingText,
        isAnimating: isAnimating || isStreaming,
    };
}
